import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../../utils/adminApi";
import { paths } from "../../paths";
import court from "../../assets/tennis-court.jpg";

const useCountUp = (endTarget, duration = 2000) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let startTime = null;
    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = timestamp - startTime;
      const currentCount = Math.min(
        Math.floor((progress / duration) * endTarget),
        endTarget
      );
      setCount(currentCount);
      if (progress < duration) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [endTarget, duration]);
  return count;
};

const AdminLogin = () => {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const athleteCount = useCountUp(6000, 2000);
  const instituteCount = useCountUp(100, 2000);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await adminApi.post("/login", {
        email: formData.email,
        password: formData.password,
      });
      localStorage.setItem("adminToken", res.data.token);
      navigate(paths.adminDashboard);
    } catch (err) {
      if (err.response) {
        // Server responded (e.g. 401) — show its message.
        setError(err.response.data?.message || "Invalid admin credentials.");
      } else {
        // No response = network/CORS/server-down. Don't blame the credentials.
        setError(
          "Can't reach the server. Make sure the backend is running and try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen w-full bg-transparent flex flex-col lg:flex-row font-sans selection:bg-black selection:text-white overflow-x-hidden overflow-y-auto lg:overflow-y-hidden">

      {/* LEFT COLUMN: Image & Statistics Card */}
      <div className="w-full lg:w-[52%] relative h-[45vh] lg:h-full p-4 lg:p-6 flex flex-col shrink-0">
        <div
          className="absolute inset-4 lg:inset-6 rounded-sm bg-cover bg-center z-0"
          style={{ backgroundImage: `url(${court})` }}
        >
          <div className="absolute inset-0 bg-black/5 rounded-sm"></div>

          <div className="hidden lg:block absolute bottom-6 left-6 right-6 z-10 bg-[#F4EFE6] p-6 rounded-sm shadow-sm border border-[#EBE3D5]">
            <div className="grid grid-cols-2 gap-6 mb-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#CC5E15]/30 flex items-center justify-center text-gray-700 shrink-0 mt-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z" />
                  </svg>
                </div>
                <div className="flex flex-col justify-center min-w-0">
                  <h3 className="text-2xl font-black text-black tracking-tight leading-none mb-1">{athleteCount}+</h3>
                  <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider leading-none">Athletes</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#CC5E15]/30 flex items-center justify-center text-gray-700 shrink-0 mt-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0 0 12 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75Z" />
                  </svg>
                </div>
                <div className="flex flex-col justify-center min-w-0">
                  <h3 className="text-2xl font-black text-black tracking-tight leading-none mb-1">{instituteCount}+</h3>
                  <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider leading-none">Institutes</p>
                </div>
              </div>
            </div>

            <hr className="border-[#E5DAC9] mb-3" />

            <p className="text-xs text-gray-600 leading-relaxed font-normal">
              Welcome to Spirit, where athletes compete, teams unite, and memories are made.
              More than a sports festival, Spirit is a platform for ambition, teamwork, and the pursuit of excellence.
            </p>
          </div>
        </div>
      </div>

      {/* RIGHT COLUMN: Admin login form */}
      <div className="w-full lg:w-[48%] h-full flex flex-col justify-center px-6 py-8 sm:px-12 md:px-20 lg:px-12 xl:px-20 max-w-xl mx-auto overflow-y-auto bg-transparent shrink-0">
        <div className="w-full my-auto">
          <div className="w-full relative max-w-[520px] mx-auto">
            {loading && (
              <div className="fixed inset-0 bg-black/20 backdrop-blur-[1px] z-50 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            <p className="text-[10px] uppercase tracking-[0.25em] text-[#CC5E15] font-bold mb-2">
              Admin Portal
            </p>
            <h1 className="text-3xl sm:text-7xl font-extrabold font-barlow text-black tracking-normal pb-2 mb-2 block clear-both">
              Admin Login
            </h1>
            <p className="text-xs text-gray-500 font-montserrat leading-relaxed mb-8">
              Sign in to manage Campus Ambassadors, tasks, submissions, and announcements.
            </p>

            <form className="space-y-6" onSubmit={handleSubmit}>
              {error && (
                <div className="text-xs text-red-600 bg-red-50 p-2 rounded-sm font-medium">
                  {error}
                </div>
              )}

              <div className="flex flex-col gap-2 w-full">
                <label className="block text-xs font-bold text-black font-montserrat tracking-wider">
                  Email Address
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="e.g. admin@spiritiitg.in"
                  required
                  className="w-full bg-[#CC5E15]/10 border-none text-gray-800 placeholder-black/40 text-xs rounded-sm px-4 py-3.5 focus:outline-none focus:ring-1 focus:ring-black/40 transition-all font-montserrat"
                />
              </div>

              <div className="flex flex-col gap-2 w-full">
                <label className="block text-xs font-bold text-black font-montserrat tracking-wider">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="e.g. *********"
                    required
                    className="w-full bg-[#CC5E15]/10 border-none text-gray-800 placeholder-black/40 text-xs rounded-sm px-4 py-3.5 pr-12 focus:outline-none focus:ring-1 focus:ring-black/40 transition-all font-mono tracking-widest placeholder:font-sans placeholder:tracking-normal"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-black/50 hover:text-black flex items-center justify-center"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-4 h-4">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-black hover:bg-gray-900 text-white font-bold text-xs py-3 rounded-sm transition-colors tracking-widest font-montserrat disabled:opacity-60"
                >
                  {loading ? "Signing in..." : "Log In"}
                </button>
              </div>
            </form>

            <div className="flex items-center justify-center gap-1 mt-4 w-full">
              <span className="text-xs text-gray-500 font-montserrat">Not an admin?</span>
              <button
                type="button"
                onClick={() => navigate(paths.login)}
                className="text-black text-xs font-bold font-montserrat underline hover:text-gray-700 transition-colors bg-transparent p-0 border-none cursor-pointer"
              >
                CA Login
              </button>
            </div>

            <div className="text-center mt-6">
              <p className="text-[9px] uppercase tracking-widest text-gray-400 font-medium">
                Together, we're redefining what student sport can be.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
